import type { CSSProperties } from 'react';
import { resolveOtherInfoObj } from '@moko/utils';

export const DEFAULT_TENANT_SUBTITLE = 'Field management system';

export type TenantBrandInfo = {
  logoUrl?: string;
  title?: string;
  titleStyle?: CSSProperties;
  /** Display text. Undefined means hide the subtitle, including the default. */
  subtitle?: string;
};

const TITLE_STYLE_KEYS = [
  'color',
  'fontSize',
  'fontWeight',
  'fontFamily',
  'fontStyle',
  'letterSpacing',
  'lineHeight',
] as const;

type TitleStyleKey = (typeof TITLE_STYLE_KEYS)[number];

const asTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

export const isSafeLogoUrl = (url: string): boolean => {
  if (/^\s*javascript:/i.test(url)) return false;
  return /^(https?:\/\/|\/)/i.test(url);
};

export const parseTitleStyle = (value: unknown): CSSProperties | undefined => {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }
    const raw = parsed as Record<string, unknown>;
    const style: CSSProperties = {};
    for (const key of TITLE_STYLE_KEYS) {
      const next = raw[key];
      if (typeof next === 'string' || typeof next === 'number') {
        (style as Record<TitleStyleKey, string | number>)[key] = next;
      }
    }
    return Object.keys(style).length > 0 ? style : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Missing/empty → default subtitle.
 * Whitespace-only (e.g. a single space) → hide the subtitle.
 * Any other string → show the trimmed custom value.
 */
export const resolveTenantSubtitle = (value: unknown): string | undefined => {
  if (value == null || typeof value !== 'string' || value.length === 0) {
    return DEFAULT_TENANT_SUBTITLE;
  }
  if (value.trim() === '') return undefined;
  return value.trim();
};

export const readTenantBrand = (otherInfo: unknown): TenantBrandInfo => {
  const info = resolveOtherInfoObj(otherInfo) ?? {};
  const logoUrl = asTrimmedString(info.logo);
  return {
    ...(logoUrl && isSafeLogoUrl(logoUrl) ? { logoUrl } : {}),
    title: asTrimmedString(info.title),
    titleStyle: parseTitleStyle(info.titleStyle),
    subtitle: resolveTenantSubtitle(info.subtitle),
  };
};
